import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, TrendingDown, Activity, Utensils, Calendar } from 'lucide-react';
import { mockGlucoseLogs, mockMeals, mockExerciseLogs } from '../mock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Analytics = () => {
  // Calculate statistics
  const last7DaysLogs = mockGlucoseLogs.slice(0, 7);
  const avgGlucose = Math.round(
    last7DaysLogs.reduce((sum, log) => sum + log.glucose_value, 0) / last7DaysLogs.length
  );
  
  const todayMeals = mockMeals.filter(meal => {
    const mealDate = new Date(meal.time);
    const today = new Date();
    return mealDate.toDateString() === today.toDateString();
  });
  
  const totalCarbsToday = todayMeals.reduce((sum, meal) => sum + meal.carbs, 0);
  const totalCaloriesToday = todayMeals.reduce((sum, meal) => sum + meal.calories, 0);
  
  const todayExercise = mockExerciseLogs.filter(ex => {
    const exDate = new Date(ex.time);
    const today = new Date();
    return exDate.toDateString() === today.toDateString();
  });
  
  const totalExerciseTime = todayExercise.reduce((sum, ex) => sum + ex.duration, 0);
  const totalCaloriesBurned = todayExercise.reduce((sum, ex) => sum + ex.calories_burned, 0);

  // Prepare data for glucose trend chart
  const glucoseTrendData = last7DaysLogs.reverse().map((log, index) => ({
    day: new Date(log.time).toLocaleDateString('en-US', { weekday: 'short' }),
    value: log.glucose_value,
    percentage: (log.glucose_value / 200) * 100
  }));

  // Daily carbs data
  const dailyCarbsData = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    const dayMeals = mockMeals.filter(meal => {
      const mealDate = new Date(meal.time);
      return mealDate.toDateString() === date.toDateString();
    });
    const carbs = dayMeals.reduce((sum, meal) => sum + meal.carbs, 0);
    return {
      day: date.toLocaleDateString('en-US', { weekday: 'short' }),
      carbs: carbs,
      percentage: (carbs / 150) * 100
    };
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white shadow-lg">
        <h2 className="text-3xl font-bold mb-2">Analytics & Trends</h2>
        <p className="text-blue-100 text-lg">Visualize your health data and track your progress</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Avg Glucose (7d)</CardTitle>
            <TrendingUp className="w-5 h-5 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{avgGlucose}</div>
            <p className="text-sm text-emerald-600 mt-1">Within target range</p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Carbs Today</CardTitle>
            <Utensils className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{totalCarbsToday}g</div>
            <p className="text-sm text-gray-600 mt-1">{totalCaloriesToday} calories</p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Exercise Today</CardTitle>
            <Activity className="w-5 h-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{totalExerciseTime} min</div>
            <p className="text-sm text-gray-600 mt-1">{totalCaloriesBurned} cal burned</p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Weekly Streak</CardTitle>
            <Calendar className="w-5 h-5 text-violet-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">7 days</div>
            <p className="text-sm text-violet-600 mt-1">Keep it up!</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="glucose" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="glucose">Glucose</TabsTrigger>
          <TabsTrigger value="nutrition">Nutrition</TabsTrigger>
          <TabsTrigger value="exercise">Exercise</TabsTrigger>
        </TabsList>

        {/* Glucose Trend */}
        <TabsContent value="glucose">
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle>7-Day Glucose Trend</CardTitle>
              <CardDescription>Your blood sugar levels over the past week</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {glucoseTrendData.map((data, index) => {
                  const isInRange = data.value >= 80 && data.value <= 130;
                  const isHigh = data.value > 130;
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-gray-700">{data.day}</span>
                        <span className={`font-bold ${
                          isInRange ? 'text-emerald-600' : isHigh ? 'text-orange-600' : 'text-red-600'
                        }`}>
                          {data.value} mg/dL
                        </span>
                      </div>
                      <div className="relative w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`absolute left-0 top-0 h-full rounded-full transition-all ${
                            isInRange 
                              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600' 
                              : isHigh 
                              ? 'bg-gradient-to-r from-orange-400 to-orange-600' 
                              : 'bg-gradient-to-r from-red-400 to-red-600'
                          }`}
                          style={{ width: `${Math.min(data.percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Target Range Indicator */}
              <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-800">
                  <strong>Target Range:</strong> 80-130 mg/dL
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  {last7DaysLogs.filter(log => log.glucose_value >= 80 && log.glucose_value <= 130).length} of {last7DaysLogs.length} readings in range ({Math.round((last7DaysLogs.filter(log => log.glucose_value >= 80 && log.glucose_value <= 130).length / last7DaysLogs.length) * 100)}%)
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Nutrition Trend */}
        <TabsContent value="nutrition">
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle>7-Day Carbohydrate Intake</CardTitle>
              <CardDescription>Daily carbs consumption pattern</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dailyCarbsData.map((data, index) => {
                  const isGood = data.carbs <= 130;
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-gray-700">{data.day}</span>
                        <span className={`font-bold ${
                          isGood ? 'text-blue-600' : 'text-orange-600'
                        }`}>
                          {data.carbs}g carbs
                        </span>
                      </div>
                      <div className="relative w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`absolute left-0 top-0 h-full rounded-full transition-all ${
                            isGood 
                              ? 'bg-gradient-to-r from-blue-400 to-blue-600' 
                              : 'bg-gradient-to-r from-orange-400 to-orange-600'
                          }`}
                          style={{ width: `${Math.min(data.percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Recommended Daily Intake:</strong> 130-150g carbs
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Average this week: {Math.round(dailyCarbsData.reduce((sum, d) => sum + d.carbs, 0) / dailyCarbsData.length)}g/day
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exercise Activity */}
        <TabsContent value="exercise">
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle>Recent Exercise Activity</CardTitle>
              <CardDescription>Your workout sessions this week</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {mockExerciseLogs.map((exercise, index) => {
                  const intensityColors = {
                    Light: 'bg-green-100 text-green-700 border-green-200',
                    Moderate: 'bg-blue-100 text-blue-700 border-blue-200',
                    Vigorous: 'bg-orange-100 text-orange-700 border-orange-200'
                  };
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
                          <Activity className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{exercise.exercise_type}</p>
                          <p className="text-sm text-gray-600">
                            {exercise.duration} mins • {exercise.calories_burned} cal burned
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${intensityColors[exercise.intensity]}`}>
                          {exercise.intensity}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(exercise.time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">
                  <strong>Weekly Goal:</strong> 150 minutes of moderate activity
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  Total this week: {mockExerciseLogs.reduce((sum, ex) => sum + ex.duration, 0)} minutes ({Math.round((mockExerciseLogs.reduce((sum, ex) => sum + ex.duration, 0) / 150) * 100)}% of goal)
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;
